const { Core } = Global;

const PARAM_PREFIX = 'clp.';
const CARD_ATTR_PREFIX = 'data-clp-f';
const VALUE_DELIMITER = '||';

customElements.define('collection-list-pro', class extends Core {

    render() {
        this._inputs = [...this.querySelectorAll('[data-clp-input]')];
        if (!this._inputs.length) return;

        this._cards = [...this.querySelectorAll('[data-element="card"]')];
        this._chipsWrap = this.querySelector('[data-element="chips"]');
        this._chipTemplate = this.querySelector('[data-element="chip-template"]');
        this._clear = this.querySelector('[data-element="clear"]');
        this._empty = this.querySelector('[data-element="empty"]');
        this._statusCount = this.querySelector('[data-element="status-count"]');

        this._groups = [...new Set(this._inputs.map(input => input.dataset.clpGroup))];
        this._paramByGroup = {};
        this._inputs.forEach(input => {
            this._paramByGroup[input.dataset.clpGroup] = input.dataset.clpParam;
        });

        // Per-card value sets, parsed once from the data-clp-f<n> attributes
        this._cardValues = this._cards.map(card => {
            const values = {};
            this._groups.forEach(group => {
                const raw = card.getAttribute(CARD_ATTR_PREFIX + group);
                values[group] = raw ? raw.split(VALUE_DELIMITER) : null;
            });
            return values;
        });

        this.$({
            change: this._handleChange,
            click: this._handleClick
        });

        this._readUrl();
        this._apply(false);
    }

    destroy() {}

    _handleChange(e) {
        const target = e.target;

        if (target.hasAttribute('data-clp-input')) {
            this._syncTwins(target);
            this._apply(true);
            return;
        }

        // Active-filter chip unchecked
        const chip = target.closest('[data-clp-chip]');
        if (chip) {
            this._setChecked(chip.dataset.clpGroup, chip.dataset.clpValue, false);
            this._apply(true);
        }
    }

    _handleClick(e) {
        if (e.target.closest('[data-element="clear"]') || e.target.closest('[data-element="empty"] a')) {
            e.preventDefault();
            this._inputs.forEach(input => input.checked = false);
            this._apply(true);
        }
    }

    // The sidebar and the mobile drawer carry duplicate checkbox sets — mirror them
    _syncTwins(changed) {
        this._inputs.forEach(input => {
            if (input !== changed
                && input.dataset.clpGroup === changed.dataset.clpGroup
                && input.value === changed.value) {
                input.checked = changed.checked;
            }
        });
    }

    _setChecked(group, value, checked) {
        this._inputs.forEach(input => {
            if (input.dataset.clpGroup === group && input.value === value) {
                input.checked = checked;
            }
        });
    }

    // Selections as { group: Set(values) }, only groups with at least one checked value
    _selections() {
        const selections = {};
        this._inputs.forEach(input => {
            if (!input.checked) return;
            (selections[input.dataset.clpGroup] ??= new Set()).add(input.value);
        });
        return selections;
    }

    // S&D facet semantics: OR within a group, AND across groups.
    // A card with no value for an actively-filtered group never matches it.
    _matches(cardIndex, selections, skipGroup) {
        return Object.entries(selections).every(([group, values]) => {
            if (group === skipGroup) return true;
            const cardValues = this._cardValues[cardIndex][group];
            if (!cardValues) return false;
            return cardValues.some(value => values.has(value));
        });
    }

    _apply(updateUrl) {
        const selections = this._selections();

        let visible = 0;
        const cardMatches = this._cards.map((card, index) => {
            const match = this._matches(index, selections);
            card.hidden = !match;
            if (match) visible += 1;
            return match;
        });

        // Facet counts: cards matching every OTHER group's selections + this value
        this._inputs.forEach(input => {
            const group = input.dataset.clpGroup;
            let count = 0;
            this._cards.forEach((card, index) => {
                const cardValues = this._cardValues[index][group];
                if (!cardValues || !cardValues.includes(input.value)) return;
                if (this._matches(index, selections, group)) count += 1;
            });
            const counter = input.closest('label')?.querySelector('[data-clp-count]');
            if (counter) counter.textContent = count;
            input.disabled = count === 0 && !input.checked;
        });

        if (this._statusCount) this._statusCount.textContent = visible;
        if (this._empty) this._empty.hidden = visible !== 0;

        this._renderChips(selections);
        if (updateUrl) this._writeUrl(selections);
    }

    _renderChips(selections) {
        if (!this._chipsWrap || !this._chipTemplate) return;

        this._chipsWrap.querySelectorAll('[data-clp-chip]').forEach(chip => chip.remove());

        let chipCount = 0;
        this._groups.forEach(group => {
            if (!selections[group]) return;
            // Chip order follows the group's input (value) order
            const seen = new Set();
            this._inputs.forEach(input => {
                if (input.dataset.clpGroup !== group || !selections[group].has(input.value) || seen.has(input.value)) return;
                seen.add(input.value);
                const chip = this._chipTemplate.content.firstElementChild.cloneNode(true);
                chip.dataset.clpGroup = group;
                chip.dataset.clpValue = input.value;
                chip.querySelector('[data-chip-label]').textContent = input.value;
                this._chipsWrap.insertBefore(chip, this._clear);
                chipCount += 1;
            });
        });

        this._chipsWrap.hidden = chipCount === 0;
        if (this._clear) this._clear.hidden = chipCount < 2;
    }

    _readUrl() {
        const params = new URLSearchParams(window.location.search);
        this._inputs.forEach(input => {
            const values = params.getAll(PARAM_PREFIX + input.dataset.clpParam);
            if (values.includes(input.value)) input.checked = true;
        });
    }

    _writeUrl(selections) {
        const url = new URL(window.location.href);
        [...url.searchParams.keys()]
            .filter(key => key.startsWith(PARAM_PREFIX))
            .forEach(key => url.searchParams.delete(key));

        this._groups.forEach(group => {
            if (!selections[group]) return;
            const seen = new Set();
            this._inputs.forEach(input => {
                if (input.dataset.clpGroup !== group || !selections[group].has(input.value) || seen.has(input.value)) return;
                seen.add(input.value);
                url.searchParams.append(PARAM_PREFIX + this._paramByGroup[group], input.value);
            });
        });

        window.history.replaceState(window.history.state, '', url);
    }
})
